<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :title="$t('resources.ab_tests.title')">
    <template #actions>
      <v-btn
        v-if="$ability.can('create', 'ab_test') && (totalItems || noData || loading)"
        class="left-icon"
        color="primary"
        to="/ab-tests/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>

    <template #default>
      <delete-resource-modal :resource-name="resourceName"></delete-resource-modal>
      <va-list
        ref="list"
        :resource="resourceName"
        :filter.sync="customFiltersValues"
        :filters="filters"
        :domains-list="domainsList"
        :goals-list="goalsList"
        :owners-list="ownersList"
        disable-export
        disable-create
        dynamic-columns-enabled
        :columns="fields"
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        @update:columns="updateColumns"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            :list-page="listPage"
            :title="$t('resources.ab_tests.list_page_empty_list_title')"
            :light-image="emptyLightImg"
            :dark-image="emptyDarkImg"
            :route="{ name: 'ab_tests_create' }">
            <template #subtitle>
              {{ $t("resources.ab_tests.list_page_empty_list_subtitle_1") }}
              <a
                href="https://help.siterunner.com/help/a-slash-b-slash-n-testing-what-is-a-slash-b-slash-n-testing-on-siterunner"
                target="_blank"
                class="help-link"
                >{{ $t("resources.ab_tests.list_page_empty_list_subtitle_link") }}</a
              >{{ $t("resources.ab_tests.list_page_empty_list_subtitle_2") }}
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            v-if="bulkSelectedItems.length < 2 && bulkSelectedItems[0].status === AB_TEST_STATUSES.ACTIVE"
            @click="changeStatus(AB_TEST_STATUSES.INACTIVE, bulkSelectedItems[0])"
            text
            color="primary">
            <v-icon small>mdi-pause-circle-outline</v-icon>
            <span class="ml-2">{{ $t("resources.ab_tests.pause") }}</span>
          </v-btn>
          <v-btn
            v-if="bulkSelectedItems.length < 2 && bulkSelectedItems[0].status !== AB_TEST_STATUSES.ACTIVE"
            @click="changeStatus(AB_TEST_STATUSES.ACTIVE, bulkSelectedItems[0])"
            text
            color="primary">
            <v-icon small>mdi-play-circle-outline</v-icon>
            <span class="ml-2">{{ $t("resources.ab_tests.activate") }}</span>
          </v-btn>
          <v-btn
            @click="editResource('ab_tests', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
        </template>
        <template #[`filter.q`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :fields="activeColumns" class="va-table--selectable ab_test-table" @row-click="handleRowClick">
          <template #[`field.name`]="{ item }">
            <v-tooltip top>
              <template #activator="{ on }">
                <div v-on="on" class="no-style text--primary cell-ellipsis">
                  <span>{{ item.name }}</span>
                </div>
              </template>
              {{ item.name }}
            </v-tooltip>
          </template>
          <template #[`field.owner.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.owner ? item.owner.firstName : "" }} {{ item.owner ? item.owner.lastName : "" }}</span>
            </div>
          </template>

          <template #[`field.statistics.callsDiff`]="{ item }">
            <MetricCard :metricData="item.statistics ? item.statistics.callsDiff : ''" />
          </template>
          <template #[`field.statistics.callsRevenueDiff`]="{ item }">
            <MetricCard :metricData="item.statistics ? item.statistics.callsRevenueDiff : ''" />
          </template>
          <template #[`field.statistics.revenueDiff`]="{ item }">
            <MetricCard :metricData="item.statistics ? item.statistics.revenueDiff : ''" />
          </template>
          <template #[`field.statistics.callsRpcDiff`]="{ item }">
            <MetricCard :metricData="item.statistics ? item.statistics.callsRpcDiff : ''" />
          </template>
          <template #[`field.statistics.callsNoConnectDiff`]="{ item }">
            <MetricCard invert-colors :metricData="item.statistics ? item.statistics.callsNoConnectDiff : ''" />
          </template>
          <template #[`field.statistics.callsUniqueCallersDiff`]="{ item }">
            <MetricCard :metricData="item.statistics ? item.statistics.callsUniqueCallersDiff : ''" />
          </template>
          <template #[`field.url`]="{ item }">
            <v-tooltip top>
              <template #activator="{ on }">
                <div v-on="on" class="no-style text--primary cell-ellipsis">
                  <span>{{ item.page ? item.page.domain.url : item.link }}</span>
                </div>
              </template>
              {{ item.page ? item.page.domain.url : item.link }}
            </v-tooltip>
          </template>
          <template #[`field.status`]="{ item }">
            <v-chip v-if="item.status === AB_TEST_STATUSES.ACTIVE" class="active-chip">{{
              $t("resources.ab_tests.active")
            }}</v-chip>
            <v-chip v-if="item.status === AB_TEST_STATUSES.INACTIVE" class="inactive-chip">{{
              $t("resources.ab_tests.inactive")
            }}</v-chip>
            <v-chip v-if="item.status === AB_TEST_STATUSES.DRAFT" class="draft-chip">{{
              $t("resources.ab_tests.draft")
            }}</v-chip>
          </template>
          <template #[`field.outcome`]="{ item }">
            <v-chip v-if="item.outcome === AB_TEST_OUTCOMES.WIN" class="success">
              {{ $t("resources.ab_tests.outcome_win") }}
            </v-chip>
            <v-chip v-else-if="item.outcome === AB_TEST_OUTCOMES.LOSS" class="error">
              {{ $t("resources.ab_tests.outcome_loss") }}
            </v-chip>
            <v-chip v-else-if="item.outcome === AB_TEST_OUTCOMES.NEED_DATA" class="warning">
              {{ $t("resources.ab_tests.outcome_need_data") }}
            </v-chip>
            <v-chip v-else-if="item.outcome === AB_TEST_OUTCOMES.INCONCLUSIVE" class="default">
              {{ $t("resources.ab_tests.outcome_inconclusive") }}
            </v-chip>
          </template>
          <template #[`field.goal.name`]="{ item }">
            <v-tooltip top v-if="item.additionalGoals.length > 0 && item.goal">
              <template #activator="{ on }">
                <div v-on="on" class="goal-wrapper">
                  <v-chip
                    :color="getChipColor(item.goal, $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.goal, $vuetify.theme.dark) }">
                    {{ item.goal.name }}
                  </v-chip>
                  {{ `+${item.additionalGoals.length}` }}
                </div>
              </template>
              {{ getAdditionalGoals(item) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.goal"
              :color="getChipColor(item.goal, $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.goal, $vuetify.theme.dark) }">
              {{ item.goal.name }}
            </v-chip>
            <v-tooltip top v-else-if="item.additionalGoals.length > 1 && !item.goal">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.additionalGoals[0], $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.additionalGoals[0], $vuetify.theme.dark) }">
                    {{ item.additionalGoals[0].name }}
                  </v-chip>
                  {{ `+${item.additionalGoals.length - 1}` }}
                </div>
              </template>
              {{ getAdditionalGoals(item, true) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.additionalGoals.length === 1"
              :color="getChipColor(item.additionalGoals[0], $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.additionalGoals[0], $vuetify.theme.dark) }">
              {{ item.additionalGoals[0].name }}
            </v-chip>
          </template>
          <template #[`field.product.name`]="{ item }">
            <v-tooltip top v-if="item.additionalProducts.length > 0 && item.product">
              <template #activator="{ on }">
                <div v-on="on" class="goal-wrapper">
                  <v-chip
                    :color="getChipColor(item.product, $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.product, $vuetify.theme.dark) }">
                    {{ item.product.name }}
                  </v-chip>
                  {{ `+${item.additionalProducts.length}` }}
                </div>
              </template>
              {{ getAdditionalProductsNames(item) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.product"
              :color="getChipColor(item.product, $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.product, $vuetify.theme.dark) }">
              {{ item.product.name }}
            </v-chip>
            <v-tooltip top v-else-if="item.additionalProducts.length > 1 && !item.product">
              <template #activator="{ on }">
                <div v-on="on" class="labels-wrapper">
                  <v-chip
                    :color="getChipColor(item.additionalProducts[0], $vuetify.theme.dark)"
                    :style="{ color: getChipTextColor(item.additionalProducts[0], $vuetify.theme.dark) }">
                    {{ item.additionalProducts[0].name }}
                  </v-chip>
                  {{ `+${item.additionalProducts.length - 1}` }}
                </div>
              </template>
              {{ getAdditionalProductsNames(item, true) }}
            </v-tooltip>
            <v-chip
              v-else-if="item.additionalProducts.length === 1"
              :color="getChipColor(item.additionalProducts[0], $vuetify.theme.dark)"
              :style="{ color: getChipTextColor(item.additionalProducts[0], $vuetify.theme.dark) }">
              {{ item.additionalProducts[0].name }}
            </v-chip>
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone), true) }}
            {{ getHoursAndMinutes(item.updatedAt) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Edit',
                  icon: 'mdi-pencil-outline',
                  function: editResource.bind(this, 'ab_tests', item.id),
                },
                ...(item.status === AB_TEST_STATUSES.ACTIVE
                  ? [
                      {
                        title: 'Pause',
                        icon: 'mdi-pause-circle-outline',
                        function: changeStatus.bind(this, AB_TEST_STATUSES.INACTIVE, item),
                      },
                    ]
                  : [
                      {
                        title: 'Activate',
                        icon: 'mdi-play-circle-outline',
                        function: changeStatus.bind(this, AB_TEST_STATUSES.ACTIVE, item),
                      },
                    ]),
                {
                  title: 'Delete',
                  icon: 'mdi-trash-can-outline',
                  function: deleteResource.bind(this, item),
                },
              ]">
            </DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import PlusIcon from "@/components/icons/PlusIcon";
  import DataListActions from "@/components/custom/DataListActions";
  import EmptyList from "@/components/custom/EmptyList";
  import MetricCard from "@/components/custom/MetricCard";
  import DeleteResourceModal from "@/components/resources/DeleteResourceModal";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import RowClick from "@/mixins/rowClickMixin";
  import emptyLightImg from "@/assets/images/empty-list/ab-tests-empty-list-light.svg";
  import emptyDarkImg from "@/assets/images/empty-list/ab-tests-empty-list-dark.svg";
  import { changeDateFormat, getHoursAndMinutes } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import { AB_TEST_UNKNOWN_DATA, AB_TEST_STATUSES, AB_TEST_OUTCOMES } from "@/constant";
  import debounce from "lodash.debounce";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { getChipColor, getChipTextColor } from "@/assets/utilities/helpers/getChipColor";

  export default {
    components: {
      ResourceListWrapper,
      PlusIcon,
      DataListActions,
      EmptyList,
      DeleteResourceModal,
      MetricCard,
    },
    mixins: [ConnectedDomainsList, RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        AB_TEST_STATUSES,
        AB_TEST_OUTCOMES,
        emptyLightImg,
        emptyDarkImg,
        noData: false,
        loading: true,
        totalItems: 0,
        customFiltersValues: this.activeFilterOnload,
        changeDateFormat,
        isUserUS,
        getHoursAndMinutes,
        getChipColor,
        getChipTextColor,
        filters: [
          {
            source: "q",
            alwaysOn: true,
          },
          {
            source: "page.domain",
            label: this.$t("resources.ab_tests.domain"),
          },
          {
            source: "status",
            resource: "ab_tests",
          },
          {
            source: "owner",
            label: this.$t("resources.ab_tests.owner"),
          },
          {
            source: "goal",
            label: this.$t("resources.ab_tests.goals"),
          },
        ],
        fields: [
          {
            source: "name",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.name"),
            full_width: "210px",
            id: "name",
          },
          {
            source: "url",
            label: this.$t("resources.ab_tests.fields.domain"),
            sortable: true,
            full_width: "130px",
            id: "page_url",
          },
          {
            source: "statistics.callsUniqueCallersDiff",
            label: this.$t("resources.ab_tests.fields.unique_callers"),
            sortable: true,
            full_width: "130px",
            id: "statistics_uniqueCallersDiff",
            align: "right",
          },
          {
            source: "statistics.callsNoConnectDiff",
            label: this.$t("resources.ab_tests.fields.no_connect"),
            sortable: true,
            full_width: "110px",
            id: "statistics_noConnectDiff",
            align: "right",
          },
          {
            source: "statistics.callsDiff",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.calls"),
            full_width: "90px",
            align: "right",
            id: "calls",
          },
          {
            source: "statistics.revenueDiff",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.revenue"),
            full_width: "90px",
            align: "right",
            id: "revenue",
          },
          {
            source: "statistics.callsRpcDiff",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.rpc"),
            full_width: "90px",
            align: "right",
            id: "rpc",
          },
          {
            source: "status",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.status"),
            full_width: "90px",
            align: "right",
            id: "status",
          },
          {
            source: "outcome",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.outcome"),
            full_width: "115px",
            align: "right",
            id: "outcome",
          },
          {
            source: "goal.name",
            label: this.$t("resources.pages.fields.goal"),
            sortable: true,
            full_width: "115px",
            cellClass: "label-cell",
            align: "right",
            id: "goal_name",
          },
          {
            source: "product.name",
            label: this.$t("resources.ab_tests.fields.product"),
            sortable: true,
            full_width: "115px",
            cellClass: "label-cell",
            align: "right",
            id: "product_name",
          },
          {
            source: "owner.name",
            label: this.$t("resources.ab_tests.fields.owner"),
            full_width: "104px",
            id: "owner_name",
            align: "right",
          },
          {
            source: "updatedAt",
            sortable: true,
            label: this.$t("resources.ab_tests.fields.last_updated"),
            full_width: "135px",
            id: "updatedAt",
            align: "right",
          },
        ],
        activeColumns: [],
        goalsList: [],
        ownersList: [],
        bulkSelectedItems: [],
      };
    },
    async beforeMount() {
      try {
        const { data } = await this.$admin.dataProvider.getList("goals", { disablePagination: true });
        this.goalsList = data;
      } catch (e) {
        this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
      }

      try {
        const { data } = await this.$admin.dataProvider.getList("users", { disablePagination: true });
        this.ownersList = data.map(user => {
          if (!user.firstName && !user.lastName) {
            return {
              ...user,
              firstName: AB_TEST_UNKNOWN_DATA,
              lastName: "",
            };
          }

          return {
            ...user,
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
          };
        });
      } catch (e) {
        this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
      }
    },
    mounted() {
      if (this.$store.state?.table?.abTest?.activeColumns?.length > 0) {
        this.fields.forEach(field => {
          if (this.$store.state.table.abTest.activeColumns.includes(field.id) || field.required) {
            this.$set(field, "active", true);
          }
        });
        this.updateColumns(this.fields, false);
      } else {
        this.$store.state.table.abTest.activeColumns = this.fields.map(field => field.id);
        this.fields = this.fields.map(field => {
          field.active = true;

          return field;
        });
        this.updateColumns(this.fields);
      }
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getAdditionalGoals(item, removeFirst = false) {
        if (removeFirst) {
          return item.additionalGoals
            ?.slice(1)
            ?.map(label => label.name)
            ?.join(", ");
        }

        return item.additionalGoals?.map(goal => goal.name).join(", ");
      },
      getAdditionalProductsNames(item, removeFirst = false) {
        if (removeFirst) {
          return item.additionalProducts
            ?.slice(1)
            ?.map(label => label.name)
            ?.join(", ");
        }

        return item.additionalProducts?.map(goal => goal.name).join(", ");
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteResource(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
        });
      },
      updateColumns(columns, updateState = true) {
        this.activeColumns = columns.filter(field => !!field.active);
        if (updateState) {
          this.$store.commit(
            "SET_AB_TEST_TABLE_COLUMNS",
            this.activeColumns.map(field => field.id),
          );
        }
        if (this.activeColumns.length !== this.fields.length) {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", undefined);
          });
        } else {
          this.activeColumns.forEach(field => {
            this.$set(field, "width", field.full_width);
          });
        }
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId } });
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
      async changeStatus(active, item) {
        try {
          await this.$admin.dataProvider.customHttpCall("PATCH", `${this.resourceName}/${item.id}`, {
            status: active,
          });
          item.status = active;
          item.active = active === AB_TEST_STATUSES.ACTIVE;
          this.$admin.toast.success(
            active === AB_TEST_STATUSES.ACTIVE
              ? this.$t("resources.ab_tests.activate_success")
              : this.$t("resources.ab_tests.pause_success"),
          );
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";

  .goal-wrapper {
    white-space: nowrap;
  }

  .ab_test-table {
    ::v-deep {
      .v-data-table {
        td,
        th {
          padding: 0px 4px;
        }

        th .v-input--checkbox {
          padding: 0 4px;
        }

        .v-data-table__checkbox {
          padding: 0 4px;
        }

        .data-list--action-btn {
          min-width: unset !important;
        }

        .item-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
        }

        .v-btn--icon {
          width: unset !important;
        }
      }
    }
  }
</style>
